import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import logo from "./images/techvanto-logo.png";

const LOGO_SRC = logo;

// ── Nav Data ──────────────────────────────────────────────────────────────────
// ── Nav Data ──────────────────────────────────────────────────────────────────
const NAV_LINKS = [
  {
    label: "Services",
    dropdown: [
      { name: "Web Development", link: "/web-development" },
      { name: "Mobile App Development", link: "/mobile-app-development" },
      { name: "AI & ML Solutions", link: "/ai-ml-solutions" },
      { name: "Cloud DevOps", link: "/cloud" },
      { name: "UI/UX Design", link: "/ui-ux" },
      { name: "E-Commerce Solutions", link: "/ecommerce" },
    ],
  },
  {
    label: "Solutions",
    dropdown: [
      { name: "CRM Solution", link: "/crm-solutions" },
      { name: "ERP Solution", link: "/erp-solutions" },
      { name: "HRM Solution", link: "/hrm-solutions" },
      { name: "LMS Solution", link: "/lms-solutions" },
      { name: "POS Solution", link: "/pos-solutions" },
      { name: "SaaS Platform", link: "/saas-platform" },
    ],
  },
  {
    label: "Industries",
      dropdown: [
      { name: "Healthcare", link: "/healthcare-solution" },
      { name: "FinTech", link: "/fintech-solution" },
      { name: "EdTech", link: "/edtech-solution" },
      { name: "Retail & E-Commerce", link: "/retail-ecommerce-solution" },
      { name: "Real Estate", link: "/realestate-solution" },
      { name: "Logistics", link: "/logistic-solution" },
    ],
  },
  { label: "Portfolio", dropdown: null, link: "/portfolio" },
  { label: "About", dropdown: null, link: "/#about" },
  { label: "Blog", dropdown: null, link: "/blogs" },
];

// ── Page Data ─────────────────────────────────────────────────────────────────

const FEATURES = [
  {
    icon: "🎓",
    title: "Course Builder & Content Authoring",
    desc: "Drag-and-drop course builder with rich multimedia support — video, SCORM, xAPI, quizzes, assignments, and interactive H5P content. Publish once, deliver everywhere: web, mobile, and offline.",
    tags: ["SCORM", "xAPI", "H5P", "Video", "Quizzes"],
    color: "#059669",
  },
  {
    icon: "🤖",
    title: "AI-Powered Adaptive Learning",
    desc: "Machine-learning engine that personalises learning paths based on each learner's performance, pace, and goals — automatically surfacing remedial content and accelerating high achievers.",
    tags: ["Adaptive Paths", "ML Engine", "Personalisation", "AI Tutor"],
    color: "#0D9488",
  },
  {
    icon: "📊",
    title: "Advanced Analytics & Reporting",
    desc: "Real-time dashboards for admins, instructors, and learners — completion rates, assessment scores, time-on-task, engagement heatmaps, and automated compliance reporting for L&D teams.",
    tags: ["Dashboards", "Compliance", "xAPI Analytics", "Heatmaps"],
    color: "#0284C7",
  },
  {
    icon: "🏆",
    title: "Gamification & Engagement",
    desc: "Points, badges, leaderboards, learning streaks, and completion certificates that turn mandatory training into a competitive, rewarding experience learners actually look forward to.",
    tags: ["Badges", "Leaderboards", "Streaks", "Certificates"],
    color: "#D97706",
  },
  {
    icon: "💬",
    title: "Live & Collaborative Learning",
    desc: "Built-in virtual classroom with Zoom / Google Meet integration, discussion forums, peer review, group projects, and real-time collaborative whiteboards for blended learning programmes.",
    tags: ["Virtual Classroom", "Zoom", "Forums", "Peer Review"],
    color: "#7C3AED",
  },
  {
    icon: "📱",
    title: "Mobile-First Learner App",
    desc: "Native iOS and Android apps with offline-sync — learners can download content and complete courses without internet, with progress synced automatically when they reconnect.",
    tags: ["iOS", "Android", "Offline Sync", "PWA"],
    color: "#DB2777",
  },
  {
    icon: "🔗",
    title: "Integrations & SSO",
    desc: "Pre-built connectors for Salesforce, SAP SuccessFactors, Workday, HubSpot, Slack, Teams, and 100+ tools via Zapier. SAML 2.0 and OAuth SSO for frictionless enterprise sign-in.",
    tags: ["Salesforce", "SAML 2.0", "Zapier", "Teams"],
    color: "#0369A1",
  },
  {
    icon: "🛒",
    title: "Course Marketplace & Monetisation",
    desc: "Built-in e-commerce for selling courses and subscription bundles — Stripe / Razorpay payments, coupon engine, affiliate tracking, and automated invoicing for B2B and B2C alike.",
    tags: ["Stripe", "Razorpay", "Subscriptions", "Affiliates"],
    color: "#B45309",
  },
];

const TECH_STACK = [
  {
    category: "Frontend",
    items: [
      { name: "React", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/react/react-original.svg", color: "#61DAFB" },
      { name: "Next.js", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/nextjs/nextjs-original.svg", color: "#000000" },
      { name: "TypeScript", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/typescript/typescript-original.svg", color: "#3178C6" },
      { name: "Tailwind CSS", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/tailwindcss/tailwindcss-original.svg", color: "#06B6D4" },
      { name: "Flutter", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/flutter/flutter-original.svg", color: "#02569B" },
      { name: "Redux", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/redux/redux-original.svg", color: "#764ABC" },
    ],
  },
  {
    category: "Backend",
    items: [
      { name: "Node.js", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/nodejs/nodejs-original.svg", color: "#339933" },
      { name: "Python", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/python/python-original.svg", color: "#3776AB" },
      { name: "Django", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/django/django-plain.svg", color: "#092E20" },
      { name: "GraphQL", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/graphql/graphql-plain.svg", color: "#E10098" },
      { name: "Laravel", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/laravel/laravel-original.svg", color: "#FF2D20" },
      { name: "WebRTC", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/javascript/javascript-original.svg", color: "#333333" },
    ],
  },
  {
    category: "Database & Storage",
    items: [
      { name: "PostgreSQL", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/postgresql/postgresql-original.svg", color: "#4169E1" },
      { name: "MongoDB", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/mongodb/mongodb-original.svg", color: "#47A248" },
      { name: "Redis", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/redis/redis-original.svg", color: "#DC382D" },
      { name: "AWS S3", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/amazonwebservices/amazonwebservices-plain-wordmark.svg", color: "#FF9900" },
      { name: "Elasticsearch", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/elasticsearch/elasticsearch-original.svg", color: "#005571" },
      { name: "Firebase", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/firebase/firebase-original.svg", color: "#FFCA28" },
    ],
  },
  {
    category: "Cloud & DevOps",
    items: [
      { name: "AWS", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/amazonwebservices/amazonwebservices-plain-wordmark.svg", color: "#FF9900" },
      { name: "Docker", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/docker/docker-original.svg", color: "#2496ED" },
      { name: "Kubernetes", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/kubernetes/kubernetes-plain.svg", color: "#326CE5" },
      { name: "Terraform", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/terraform/terraform-original.svg", color: "#7B42BC" },
      { name: "GitHub Actions", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/github/github-original.svg", color: "#181717" },
      { name: "Cloudflare", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/cloudflare/cloudflare-original.svg", color: "#F38020" },
    ],
  },
];

const PROCESS = [
  { n: "01", icon: "🔍", title: "Discovery & L&D Audit", desc: "We map your existing training programmes, learner personas, content inventory, and compliance requirements — building a platform blueprint aligned with your organisation's unique learning culture.", color: "#059669" },
  { n: "02", icon: "🏗️", title: "Architecture & UX Design", desc: "Multi-tenant architecture design, learner journey mapping, admin portal wireframes, and mobile-first UI design. We prototype the learner experience before writing a line of backend code.", color: "#0D9488" },
  { n: "03", icon: "⚙️", title: "Platform Development", desc: "Agile sprints with bi-weekly demos — core LMS engine first, then features in priority order. Full test coverage (unit, integration, accessibility) at every sprint boundary.", color: "#0284C7" },
  { n: "04", icon: "📦", title: "Content Migration", desc: "Automated migration of existing SCORM packages, videos, PDFs, and assessments into the new platform — with content audit, quality checks, and metadata enrichment for searchability.", color: "#D97706" },
  { n: "05", icon: "🧪", title: "UAT & Pilot Launch", desc: "Controlled pilot with a cohort of real learners — collecting behavioural data, running usability sessions, fixing edge cases, and load-testing to 10× expected concurrent users before full rollout.", color: "#7C3AED" },
  { n: "06", icon: "🚀", title: "Go-Live & Ongoing Growth", desc: "Phased rollout with admin training, helpdesk handover, and a 90-day hypercare period. Ongoing iteration based on analytics — new features, integrations, and content additions on retainer.", color: "#DB2777" },
];

const CASE_STUDIES = [
  {
    title: "EduNation — National EdTech Platform",
    industry: "EdTech",
    desc: "Built a white-label LMS serving 1.2M K-12 students across 3,000+ schools — with adaptive quizzes, regional-language support in 8 languages, offline mobile app, and a live virtual classroom used by 40,000 teachers daily.",
    img: "https://images.unsplash.com/photo-1580582932707-520aed937b7b?w=600&h=400&fit=crop&auto=format",
    tags: ["React", "Node.js", "AWS", "Flutter", "WebRTC"],
    metrics: [{ val: "1.2M", lbl: "Active Learners" }, { val: "8", lbl: "Languages Supported" }, { val: "40k+", lbl: "Daily Teachers" }],
    color: "#059669",
  },
  {
    title: "SkillBridge — Corporate L&D Suite",
    industry: "Corporate Training",
    desc: "Custom enterprise LMS for a Fortune 500 FMCG company — mandatory compliance training, role-based learning paths, Workday SSO, SCORM 1.2/2004 support, and real-time completion dashboards for 28,000 employees across 14 countries.",
    img: "https://images.unsplash.com/photo-1531482615713-2afd69097998?w=600&h=400&fit=crop&auto=format",
    tags: ["SCORM", "SAML SSO", "Workday", "PostgreSQL"],
    metrics: [{ val: "28k", lbl: "Employees Trained" }, { val: "14", lbl: "Countries" }, { val: "99.8%", lbl: "Compliance Rate" }],
    color: "#0D9488",
  },
  {
    title: "ProCert Academy — Course Marketplace",
    industry: "Professional Certification",
    desc: "End-to-end course marketplace for a professional certification body — instructor portal, Stripe payments, certificate generation with blockchain verification, and an AI-powered study assistant that boosted exam pass rates by 34%.",
    img: "https://images.unsplash.com/photo-1434030216411-0b793f4b4173?w=600&h=400&fit=crop&auto=format",
    tags: ["Next.js", "Stripe", "Blockchain", "AI Tutor"],
    metrics: [{ val: "34%", lbl: "Higher Pass Rates" }, { val: "₹8Cr+", lbl: "Revenue Generated" }, { val: "4.8★", lbl: "Learner Rating" }],
    color: "#0284C7",
  },
];

const FAQS = [
  {
    q: "Can your LMS support both B2B corporate training and B2C course selling?",
    a: "Absolutely. Our LMS architecture supports multi-tenant deployments where the same platform serves internal employees (B2B) and external customers (B2C) with completely separate portals, branding, pricing, and reporting. You can run a mandatory compliance programme for staff while simultaneously selling professional courses to external learners — from a single admin dashboard.",
  },
  {
    q: "Is your LMS SCORM and xAPI compliant?",
    a: "Yes, we support SCORM 1.2, SCORM 2004 (all 4 editions), xAPI (Tin Can), AICC, and cmi5 out of the box. Our SCORM player is built to the specification and tested against common authoring tools (Articulate Storyline, Rise, Adobe Captivate, iSpring). xAPI statements are stored in a bundled LRS or can be piped to your external Learning Record Store.",
  },
  {
    q: "How does the adaptive learning engine work?",
    a: "Our AI engine builds a knowledge graph of each course topic and maps each learner's mastery level continuously. When a learner struggles on an assessment, the engine automatically serves targeted remedial micro-lessons before allowing progression. High-performers are fast-tracked past content they've already demonstrated mastery of. The system learns from cohort-level patterns to improve recommendations over time.",
  },
  {
    q: "Can the LMS handle large-scale concurrent users?",
    a: "Yes. The platform is built on auto-scaling Kubernetes clusters with CDN-delivered video (via AWS CloudFront or Cloudflare). We load-test every deployment to 10× peak concurrent users before go-live. Our largest deployment serves 1.2 million registered learners with up to 80,000 concurrent active sessions during exam periods — with 99.95% uptime.",
  },
  {
    q: "What white-labelling and custom branding options are available?",
    a: "Full white-label with your domain, logo, colour scheme, typography, email templates, certificate designs, and learner app store listings (iOS/Android) published under your brand. You can also customise the learner portal layout, homepage modules, navigation structure, and notification copy. Admins control all branding settings from the dashboard without developer involvement.",
  },
  {
    q: "What post-launch support do you offer?",
    a: "All LMS deployments include a 90-day hypercare period with dedicated support. After that, we offer tiered SLA support packages: Standard (business-hours, 24h response), Professional (extended hours, 4h response), and Enterprise (24/7, 1h response with a named account engineer). We also offer ongoing development retainers for feature additions, new integrations, and content-type expansions.",
  },
];

const STATS = [
  { val: "50+", lbl: "LMS Platforms Built", icon: "🎓" },
  { val: "5M+", lbl: "Learners Served", icon: "👩‍🎓" },
  { val: "99.95%", lbl: "Platform Uptime", icon: "⚡" },
  { val: "34%", lbl: "Avg Completion ↑", icon: "📈" },
];

const USE_CASES = [
  { industry: "EdTech", icon: "📚", examples: ["K-12 & higher education LMS", "Adaptive assessment engine", "Multi-language content delivery"], color: "#059669" },
  { industry: "Corporate L&D", icon: "🏢", examples: ["Compliance & onboarding training", "Role-based learning paths", "Workday / SAP integration"], color: "#0D9488" },
  { industry: "Healthcare", icon: "🏥", examples: ["CME & clinical training", "HIPAA-compliant platform", "Certification & re-certification"], color: "#0284C7" },
  { industry: "Professional Certs", icon: "🏆", examples: ["Course marketplace & monetisation", "Proctored online exams", "Blockchain-verified certificates"], color: "#D97706" },
  { industry: "Government", icon: "🏛️", examples: ["Civil service training portal", "Multi-department LMS", "Accessibility & WCAG compliance"], color: "#7C3AED" },
  { industry: "Retail & FMCG", icon: "🛍️", examples: ["Franchise staff onboarding", "Product knowledge training", "Sales enablement LMS"], color: "#DB2777" },
];

// ── Navbar ────────────────────────────────────────────────────────────────────
// ── Navbar ────────────────────────────────────────────────────────────────────
function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);

  // Smart logic to check which page we are currently on
  const currentPath = typeof window !== 'undefined' ? window.location.pathname : '/';

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <nav style={{
      position: "fixed", top: 0, left: 0, right: 0, zIndex: 1000,
      background: scrolled ? "rgba(255,255,255,0.98)" : "rgba(255,255,255,0.93)",
      backdropFilter: "blur(20px)",
      borderBottom: scrolled ? "1px solid #D1FAE5" : "1px solid transparent",
      boxShadow: scrolled ? "0 2px 32px rgba(5,150,105,.07)" : "none",
      transition: "all .3s ease",
    }}>
      <div style={{
        background: "linear-gradient(90deg,#064E3B,#059669,#0D9488)",
        color: "#fff", fontSize: 12, padding: "6px 40px",
        display: "flex", justifyContent: "space-between", alignItems: "center",
        flexWrap: "wrap", gap: 8,
      }}>
        <div style={{ display: "flex", gap: 20, flexWrap: "wrap" }}>
          <span>📞 +91 96462 06032</span>
          <span>✉️ info@techvanto.com</span>
        </div>
        <span className="topbar-right" style={{ opacity: .9 }}>🌏 Trusted by 1500+ clients across 20+ countries</span>
      </div>
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        padding: "0 40px", height: 68, maxWidth: 1280, margin: "0 auto",
      }}>
        <a href="/" style={{ display: "flex", alignItems: "center", flexShrink: 0, textDecoration: "none" }}>
          <img src={LOGO_SRC} alt="Techvanto" style={{ height: 150, width: "auto", objectFit: "contain", display: "block" }} />
        </a>
        <div className="nav-links" style={{ alignItems: "center", gap: 4 }}>
          {NAV_LINKS.map(link => {
            // Check if this specific main link is the active page
            const isActive = link.link && currentPath.includes(link.label.toLowerCase());

            return (
              <div key={link.label} style={{ position: "relative" }}
                onMouseEnter={() => link.dropdown && setActiveDropdown(link.label)}
                onMouseLeave={() => setActiveDropdown(null)}
              >
                {link.link ? (
                  <a href={link.link} style={{
                    background: isActive ? "#ECFDF5" : "none",
                    border: isActive ? "1px solid #A7F3D0" : "1px solid transparent",
                    cursor: "pointer", padding: "8px 14px", fontSize: 14, fontWeight: 600,
                    color: isActive ? "#065F46" : "#374151",
                    display: "flex", alignItems: "center", gap: 4, transition: "color .2s, background .2s",
                    fontFamily: "'DM Sans',sans-serif", borderRadius: 8, textDecoration: "none"
                  }}>
                    {link.label}
                  </a>
                ) : (
                  <button style={{
                    // Highlight the "Solutions" parent button if we are on the lms-solutions page
                    background: (link.label === "Solutions" && currentPath === "/lms-solutions") ? "#ECFDF5" : "none",
                    border: (link.label === "Solutions" && currentPath === "/lms-solutions") ? "1px solid #A7F3D0" : "1px solid transparent",
                    cursor: "pointer", padding: "8px 14px", fontSize: 14, fontWeight: 600,
                    color: (link.label === "Solutions" && currentPath === "/lms-solutions") ? "#065F46" : activeDropdown === link.label ? "#065F46" : "#374151",
                    display: "flex", alignItems: "center", gap: 4, transition: "color .2s",
                    fontFamily: "'DM Sans',sans-serif", borderRadius: 8,
                  }}>
                    {link.label}
                    {link.dropdown && <span style={{ fontSize: 10, transition: "transform .2s", transform: activeDropdown === link.label ? "rotate(180deg)" : "rotate(0)" }}>▾</span>}
                  </button>
                )}
                {link.dropdown && activeDropdown === link.label && (
                  <div style={{
                    position: "absolute", top: "100%", left: 0, zIndex: 200,
                    background: "#fff", border: "1px solid #D1FAE5", borderRadius: 16,
                    boxShadow: "0 20px 60px rgba(5,150,105,.12)", minWidth: 230,
                    padding: "8px 0", animation: "fadeIn .2s ease",
                  }}>
                    {link.dropdown.map(item => (
                      <a key={item.name} href={item.link} style={{
                        display: "block", padding: "10px 20px", fontSize: 13.5,
                        // Highlight the active item inside the dropdown
                        color: currentPath === item.link ? "#065F46" : "#374151",
                        background: currentPath === item.link ? "#ECFDF5" : "none",
                        textDecoration: "none", fontWeight: currentPath === item.link ? 700 : 500,
                        transition: "all .15s",
                      }}
                        onMouseEnter={e => { e.target.style.background = "#ECFDF5"; e.target.style.color = "#065F46"; }}
                        onMouseLeave={e => { 
                          e.target.style.background = currentPath === item.link ? "#ECFDF5" : "none"; 
                          e.target.style.color = currentPath === item.link ? "#065F46" : "#374151"; 
                        }}
                      >{item.name}</a>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
          <a href="#contact" className="nav-cta" style={{
            background: "linear-gradient(135deg,#064E3B,#059669)", color: "#fff",
            padding: "10px 22px", borderRadius: 10, fontSize: 13.5, fontWeight: 700,
            textDecoration: "none", boxShadow: "0 4px 16px rgba(5,150,105,.3)",
            transition: "transform .2s, box-shadow .2s", fontFamily: "'DM Sans',sans-serif",
          }}
            onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-1px)"; e.currentTarget.style.boxShadow = "0 8px 24px rgba(5,150,105,.42)"; }}
            onMouseLeave={e => { e.currentTarget.style.transform = "none"; e.currentTarget.style.boxShadow = "0 4px 16px rgba(5,150,105,.3)"; }}
          >Get Free Demo</a>
          <button onClick={() => setMobileOpen(!mobileOpen)} className="mob-menu-btn" style={{
            background: "none", border: "1px solid #D1FAE5", borderRadius: 8,
            padding: "8px 12px", cursor: "pointer", fontSize: 18, color: "#374151",
          }}>☰</button>
        </div>
      </div>
      {mobileOpen && (
        <div style={{ background: "#fff", borderTop: "1px solid #D1FAE5", padding: "16px 24px 24px", display: "flex", flexDirection: "column", gap: 4 }}>
          <div style={{ paddingBottom: 14, borderBottom: "1px solid #ECFDF5", marginBottom: 4 }}>
            <img src={LOGO_SRC} alt="Techvanto" style={{ height: 40, width: "auto", objectFit: "contain" }} />
          </div>
          {NAV_LINKS.map(l => (
            <a key={l.label} href={l.link || "#"} onClick={() => setMobileOpen(false)} style={{ padding: "10px 0", color: currentPath.includes(l.label.toLowerCase()) ? "#065F46" : "#374151", fontWeight: 600, textDecoration: "none", borderBottom: "1px solid #ECFDF5", fontFamily: "'DM Sans',sans-serif" }}>{l.label}</a>
          ))}
          <a href="#contact" onClick={() => setMobileOpen(false)} style={{ marginTop: 12, background: "linear-gradient(135deg,#064E3B,#059669)", color: "#fff", padding: "13px", borderRadius: 10, textAlign: "center", fontWeight: 700, textDecoration: "none" }}>Get Free Demo</a>
        </div>
      )}
    </nav>
  );
}

// ── Hero ──────────────────────────────────────────────────────────────────────
function Hero() {
  const [counter, setCounter] = useState(0);
  const metrics = ["5M+ Learners Served", "34% Higher Completion Rates", "99.95% Platform Uptime", "50+ LMS Platforms Built"];

  useEffect(() => {
    const t = setInterval(() => setCounter(c => (c + 1) % metrics.length), 2400);
    return () => clearInterval(t);
  }, []);

  return (
    <section style={{
      paddingTop: 148, paddingBottom: 100,
      background: "linear-gradient(150deg,#022C22 0%,#064E3B 30%,#065F46 60%,#0F172A 100%)",
      position: "relative", overflow: "hidden",
    }}>
      {/* Dot-grid pattern */}
      <div style={{
        position: "absolute", inset: 0, pointerEvents: "none",
        backgroundImage: "radial-gradient(rgba(52,211,153,.18) 1.5px, transparent 1.5px)",
        backgroundSize: "36px 36px",
      }} />

      {/* Floating geometric knowledge shapes */}
      <div style={{ position: "absolute", inset: 0, pointerEvents: "none" }}>
        {[
          { size: 90, top: "8%", left: "5%", delay: "0s", shape: "8px" },
          { size: 60, top: "18%", right: "8%", delay: "1.2s", shape: "50%" },
          { size: 130, bottom: "12%", left: "12%", delay: "0.6s", shape: "16px" },
          { size: 45, top: "60%", right: "4%", delay: "1.8s", shape: "50%" },
          { size: 75, bottom: "25%", right: "18%", delay: "0.9s", shape: "12px" },
          { size: 50, top: "35%", left: "2%", delay: "2.1s", shape: "50%" },
        ].map((s, i) => (
          <div key={i} style={{
            position: "absolute", width: s.size, height: s.size,
            borderRadius: s.shape,
            border: "1.5px solid rgba(52,211,153,.12)",
            top: s.top, left: s.left, right: s.right, bottom: s.bottom,
            animation: `floatSlow ${7 + i}s ease-in-out infinite ${s.delay}`,
            background: "rgba(5,150,105,.04)",
          }} />
        ))}
      </div>

      {/* Glow orbs */}
      <div style={{ position: "absolute", top: -150, right: -80, width: 750, height: 750, borderRadius: "50%", background: "radial-gradient(circle,rgba(5,150,105,.2) 0%,rgba(13,148,136,.06) 45%,transparent 70%)", pointerEvents: "none" }} />
      <div style={{ position: "absolute", bottom: -120, left: -100, width: 580, height: 580, borderRadius: "50%", background: "radial-gradient(circle,rgba(2,132,199,.12),transparent 65%)", pointerEvents: "none" }} />

      <div style={{ maxWidth: 1280, margin: "0 auto", padding: "0 40px", position: "relative" }}>
        {/* Breadcrumb */}
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 32, fontSize: 13, color: "rgba(255,255,255,.45)" }}>
          <Link to="/" style={{ color: "#6EE7B7", textDecoration: "none", fontWeight: 600 }}>Home</Link>
          <span>›</span><span>Solutions</span><span>›</span>
          <span style={{ color: "#fff", fontWeight: 600 }}>LMS Solution</span>
        </div>

        <div className="lms-hero-grid">
          {/* Left */}
          <div>
            <div style={{ display: "inline-flex", alignItems: "center", gap: 8, background: "rgba(5,150,105,.2)", border: "1px solid rgba(110,231,183,.35)", borderRadius: 99, padding: "6px 18px", marginBottom: 28 }}>
              <span style={{ width: 8, height: 8, borderRadius: "50%", background: "#6EE7B7", display: "inline-block", animation: "pulse 2s infinite" }} />
              <span style={{ fontSize: 13, fontWeight: 700, color: "#6EE7B7", letterSpacing: ".02em", fontFamily: "'DM Sans',sans-serif" }}>🎓 India's #1 Custom LMS Builder</span>
            </div>

            <h1 style={{
              fontFamily: "'Fraunces',serif", fontSize: "clamp(34px,4.5vw,60px)", fontWeight: 900,
              color: "#fff", lineHeight: 1.06, marginBottom: 22, letterSpacing: "-0.025em",
            }}>
              The Learning Platform<br />
              <span style={{ background: "linear-gradient(135deg,#6EE7B7 0%,#34D399 35%,#22D3EE 80%)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundClip: "text" }}>
                Your Learners Deserve.
              </span>
            </h1>
            <p style={{ fontSize: 17, color: "rgba(255,255,255,.65)", lineHeight: 1.85, maxWidth: 570, marginBottom: 36, fontFamily: "'DM Sans',sans-serif" }}>
              We build bespoke, scalable Learning Management Systems — from AI-adaptive EdTech platforms and enterprise compliance suites to course marketplaces — that turn training into measurable business impact.
            </p>

            {/* Rotating metric */}
            <div style={{ background: "rgba(5,150,105,.15)", border: "1px solid rgba(110,231,183,.28)", borderRadius: 14, padding: "14px 22px", marginBottom: 34, display: "inline-flex", alignItems: "center", gap: 12 }}>
              <span style={{ fontSize: 18 }}>✨</span>
              <span style={{ fontFamily: "'DM Sans',sans-serif", fontWeight: 700, fontSize: 15, color: "#6EE7B7" }}>{metrics[counter]}</span>
            </div>

            <div style={{ display: "flex", gap: 14, flexWrap: "wrap", marginBottom: 46 }}>
              <a href="#contact" style={{
                background: "linear-gradient(135deg,#064E3B,#059669,#0D9488)", color: "#fff",
                padding: "15px 32px", borderRadius: 12, fontWeight: 700, fontSize: 15,
                textDecoration: "none", boxShadow: "0 8px 28px rgba(5,150,105,.45)",
                transition: "transform .2s,box-shadow .2s", fontFamily: "'DM Sans',sans-serif",
              }}
                onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = "0 12px 36px rgba(5,150,105,.55)"; }}
                onMouseLeave={e => { e.currentTarget.style.transform = "none"; e.currentTarget.style.boxShadow = "0 8px 28px rgba(5,150,105,.45)"; }}
              >🚀 Get Free Demo</a>
              <a href="#case-studies" style={{
                background: "rgba(255,255,255,.07)", color: "#fff",
                padding: "15px 30px", borderRadius: 12, fontWeight: 700, fontSize: 15,
                textDecoration: "none", border: "1px solid rgba(255,255,255,.18)",
                transition: "all .2s", fontFamily: "'DM Sans',sans-serif",
              }}
                onMouseEnter={e => { e.currentTarget.style.background = "rgba(255,255,255,.14)"; }}
                onMouseLeave={e => { e.currentTarget.style.background = "rgba(255,255,255,.07)"; }}
              >📊 See Case Studies</a>
            </div>
            <div style={{ display: "flex", gap: 32, flexWrap: "wrap" }}>
              {[["50+", "LMS Built"], ["5M+", "Learners"], ["99.95%", "Uptime"], ["34%", "Completion ↑"]].map(([v, l]) => (
                <div key={l}>
                  <div style={{ fontFamily: "'Fraunces',serif", fontWeight: 900, fontSize: 22, color: "#6EE7B7", letterSpacing: "-0.02em" }}>{v}</div>
                  <div style={{ fontSize: 11.5, color: "rgba(255,255,255,.38)", fontWeight: 600, marginTop: 2, fontFamily: "'DM Sans',sans-serif" }}>{l}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Right — LMS Visual */}
          <div style={{ position: "relative", display: "flex", justifyContent: "center", alignItems: "center" }}>
            {/* Central icon */}
            <div style={{
              width: 270, height: 270, borderRadius: "30% 70% 70% 30% / 30% 30% 70% 70%",
              background: "linear-gradient(135deg,rgba(5,150,105,.28),rgba(13,148,136,.15))",
              border: "1.5px solid rgba(110,231,183,.28)",
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: 96, animation: "morphFloat 6s ease-in-out infinite",
              boxShadow: "0 0 100px rgba(5,150,105,.2), inset 0 1px 0 rgba(255,255,255,.08)",
              position: "relative", zIndex: 2,
              backdropFilter: "blur(12px)",
            }}>🎓</div>

            {/* Orbiting capability chips */}
            {[
              { label: "Adaptive Learning", angle: 0, color: "#6EE7B7" },
              { label: "SCORM / xAPI", angle: 60, color: "#34D399" },
              { label: "Live Classroom", angle: 120, color: "#22D3EE" },
              { label: "Gamification", angle: 180, color: "#FCD34D" },
              { label: "Analytics", angle: 240, color: "#C4B5FD" },
              { label: "Mobile App", angle: 300, color: "#F9A8D4" },
            ].map((chip, i) => {
              const rad = (chip.angle * Math.PI) / 180;
              const r = 185;
              const x = Math.cos(rad) * r;
              const y = Math.sin(rad) * r;
              return (
                <div key={chip.label} style={{
                  position: "absolute",
                  left: `calc(50% + ${x}px)`, top: `calc(50% + ${y}px)`,
                  transform: "translate(-50%,-50%)",
                  background: "rgba(2,44,34,.9)",
                  border: `1px solid ${chip.color}50`,
                  borderRadius: 99,
                  padding: "8px 16px",
                  fontSize: 12, fontWeight: 700,
                  color: chip.color,
                  whiteSpace: "nowrap",
                  boxShadow: `0 4px 20px ${chip.color}20`,
                  zIndex: 3,
                  backdropFilter: "blur(8px)",
                  animation: `float ${3.5 + i * 0.3}s ease-in-out infinite ${i * 0.4}s`,
                  fontFamily: "'DM Sans',sans-serif",
                }}>{chip.label}</div>
              );
            })}

            {/* Floating metric cards */}
            <div style={{ position: "absolute", top: -10, right: -20, background: "rgba(255,255,255,.97)", borderRadius: 18, padding: "14px 20px", boxShadow: "0 16px 48px rgba(5,150,105,.22)", border: "1.5px solid #A7F3D0", animation: "float 3s ease-in-out infinite", zIndex: 10 }}>
              <div style={{ fontFamily: "'Fraunces',serif", fontWeight: 900, fontSize: 20, color: "#065F46", letterSpacing: "-0.02em" }}>5M+</div>
              <div style={{ fontSize: 11, color: "#9CA3AF", fontWeight: 500, marginTop: 1, fontFamily: "'DM Sans',sans-serif" }}>Learners Served 👩‍🎓</div>
            </div>
            <div style={{ position: "absolute", bottom: 24, right: -16, background: "linear-gradient(135deg,#064E3B,#059669)", borderRadius: 18, padding: "14px 20px", boxShadow: "0 12px 36px rgba(5,150,105,.4)", animation: "float 3.5s ease-in-out infinite .5s", zIndex: 10, textAlign: "center" }}>
              <div style={{ fontFamily: "'Fraunces',serif", fontWeight: 900, fontSize: 20, color: "#fff", letterSpacing: "-0.02em" }}>99.95%</div>
              <div style={{ fontSize: 11, color: "rgba(255,255,255,.8)", fontWeight: 600, fontFamily: "'DM Sans',sans-serif" }}>Uptime ⚡</div>
            </div>
            <div style={{ position: "absolute", bottom: 44, left: -16, background: "rgba(255,255,255,.97)", borderRadius: 16, padding: "12px 16px", boxShadow: "0 8px 28px rgba(0,0,0,.12)", border: "1.5px solid #ECFDF5", animation: "float 4s ease-in-out infinite 1s", zIndex: 10 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ fontSize: 20 }}>📈</span>
                <div>
                  <div style={{ fontWeight: 800, fontSize: 13, color: "#065F46", fontFamily: "'Fraunces',serif" }}>34% Higher</div>
                  <div style={{ fontSize: 10, color: "#9CA3AF", fontFamily: "'DM Sans',sans-serif" }}>Completion Rates</div>
                </div>
              </div>
            </div>
            <div style={{ position: "absolute", top: 55, left: -20, background: "rgba(255,255,255,.97)", borderRadius: 14, padding: "10px 14px", boxShadow: "0 6px 20px rgba(13,148,136,.18)", border: "1.5px solid #CCFBF1", animation: "float 4.5s ease-in-out infinite 1.5s", zIndex: 10 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
                <span style={{ fontSize: 16 }}>🏆</span>
                <div>
                  <div style={{ fontWeight: 800, fontSize: 12, color: "#0F766E", fontFamily: "'Fraunces',serif" }}>SCORM 2004</div>
                  <div style={{ fontSize: 10, color: "#9CA3AF", fontFamily: "'DM Sans',sans-serif" }}>Fully Compliant</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ── Stats Bar ─────────────────────────────────────────────────────────────────
function StatsBar() {
  return (
    <section style={{ background: "#011A12", padding: "52px 40px", borderBottom: "1px solid rgba(5,150,105,.15)" }}>
      <div style={{ maxWidth: 1280, margin: "0 auto", display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 32, textAlign: "center" }} className="lms-stats-grid">
        {STATS.map(s => (
          <div key={s.lbl}>
            <div style={{ fontSize: 28, marginBottom: 8 }}>{s.icon}</div>
            <div style={{
              fontFamily: "'Fraunces',serif",
              fontSize: "clamp(28px,3vw,42px)", fontWeight: 900,
              background: "linear-gradient(135deg,#6EE7B7,#22D3EE)",
              WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundClip: "text",
              lineHeight: 1, letterSpacing: "-0.025em",
            }}>{s.val}</div>
            <div style={{ color: "#6B7280", fontSize: 13, marginTop: 7, fontWeight: 500, fontFamily: "'DM Sans',sans-serif" }}>{s.lbl}</div>
          </div>
        ))}
      </div>
    </section>
  );
}

// ── Features Grid ─────────────────────────────────────────────────────────────
function FeaturesGrid() {
  const [hov, setHov] = useState(null);
  return (
    <section id="features" style={{ padding: "100px 40px", background: "#fff" }} className="lms-section">
      <div style={{ maxWidth: 1280, margin: "0 auto" }}>
        <div style={{ textAlign: "center", marginBottom: 68 }}>
          <span style={{ fontSize: 12, fontWeight: 700, color: "#059669", letterSpacing: ".12em", textTransform: "uppercase", fontFamily: "'DM Sans',sans-serif" }}>Platform Features</span>
          <h2 style={{ fontFamily: "'Fraunces',serif", fontSize: "clamp(28px,4vw,46px)", fontWeight: 900, color: "#0F172A", marginTop: 10, marginBottom: 16, letterSpacing: "-0.025em" }}>Everything Your LMS Needs to Succeed</h2>
          <p style={{ color: "#6B7280", fontSize: 16, maxWidth: 560, margin: "0 auto", lineHeight: 1.8, fontFamily: "'DM Sans',sans-serif" }}>
            A complete, modern learning platform — not a checklist of barely-functional features, but a coherent system designed to make learning stick.
          </p>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(300px,1fr))", gap: 24 }}>
          {FEATURES.map((s, i) => (
            <div key={s.title}
              onMouseEnter={() => setHov(i)}
              onMouseLeave={() => setHov(null)}
              style={{
                background: "#fff", borderRadius: 24, padding: "34px 30px",
                border: `2px solid ${hov === i ? s.color : "#F0FDF4"}`,
                boxShadow: hov === i ? `0 28px 70px ${s.color}1E` : "0 2px 16px rgba(0,0,0,.04)",
                transition: "all .32s cubic-bezier(.4,0,.2,1)",
                transform: hov === i ? "translateY(-8px)" : "none",
                position: "relative", overflow: "hidden",
              }}>
              <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 3, background: `linear-gradient(90deg,${s.color},${s.color}55)`, transform: hov === i ? "scaleX(1)" : "scaleX(0)", transformOrigin: "left", transition: "transform .32s ease" }} />
              <div style={{ width: 58, height: 58, borderRadius: 16, background: `${s.color}12`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 26, marginBottom: 22, border: `1.5px solid ${s.color}22` }}>
                {s.icon}
              </div>
              <h3 style={{ fontFamily: "'Fraunces',serif", fontSize: 19, fontWeight: 800, color: "#0F172A", marginBottom: 10, letterSpacing: "-0.02em" }}>{s.title}</h3>
              <p style={{ color: "#6B7280", fontSize: 14, lineHeight: 1.8, marginBottom: 20, fontFamily: "'DM Sans',sans-serif" }}>{s.desc}</p>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 18 }}>
                {s.tags.map(t => (
                  <span key={t} style={{ background: `${s.color}0E`, color: s.color, fontSize: 11, fontWeight: 700, padding: "4px 11px", borderRadius: 99, border: `1px solid ${s.color}22`, fontFamily: "'DM Sans',sans-serif" }}>{t}</span>
                ))}
              </div>
              <div style={{ color: s.color, fontWeight: 700, fontSize: 13, opacity: hov === i ? 1 : 0, transition: "opacity .22s", fontFamily: "'DM Sans',sans-serif" }}>Explore Feature →</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ── Use Cases by Industry ─────────────────────────────────────────────────────
function UseCases() {
  const [hov, setHov] = useState(null);
  return (
    <section style={{ padding: "100px 40px", background: "#F0FDF4" }} className="lms-section">
      <div style={{ maxWidth: 1280, margin: "0 auto" }}>
        <div style={{ textAlign: "center", marginBottom: 68 }}>
          <span style={{ fontSize: 12, fontWeight: 700, color: "#059669", letterSpacing: ".12em", textTransform: "uppercase", fontFamily: "'DM Sans',sans-serif" }}>Use Cases</span>
          <h2 style={{ fontFamily: "'Fraunces',serif", fontSize: "clamp(28px,4vw,46px)", fontWeight: 900, color: "#0F172A", marginTop: 10, marginBottom: 16, letterSpacing: "-0.025em" }}>LMS for Every Learning Context</h2>
          <p style={{ color: "#6B7280", fontSize: 16, maxWidth: 520, margin: "0 auto", lineHeight: 1.8, fontFamily: "'DM Sans',sans-serif" }}>From kindergarten to C-suite — domain-specific LMS builds that speak the language of your learners.</p>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(300px,1fr))", gap: 24 }}>
          {USE_CASES.map((uc, i) => (
            <div key={uc.industry}
              onMouseEnter={() => setHov(i)}
              onMouseLeave={() => setHov(null)}
              style={{
                background: "#fff", borderRadius: 24, padding: "30px 26px",
                border: `2px solid ${hov === i ? uc.color : "#D1FAE5"}`,
                boxShadow: hov === i ? `0 20px 56px ${uc.color}16` : "0 2px 8px rgba(0,0,0,.04)",
                transition: "all .3s", transform: hov === i ? "translateY(-5px)" : "none",
              }}>
              <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 22 }}>
                <div style={{ width: 54, height: 54, borderRadius: 16, background: `${uc.color}12`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 24, border: `1.5px solid ${uc.color}1E` }}>
                  {uc.icon}
                </div>
                <h3 style={{ fontFamily: "'Fraunces',serif", fontWeight: 800, fontSize: 18, color: "#0F172A", letterSpacing: "-0.02em" }}>{uc.industry}</h3>
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 11 }}>
                {uc.examples.map(ex => (
                  <div key={ex} style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <span style={{ width: 7, height: 7, borderRadius: "50%", background: uc.color, flexShrink: 0, display: "inline-block" }} />
                    <span style={{ fontSize: 13.5, color: "#374151", fontWeight: 500, fontFamily: "'DM Sans',sans-serif" }}>{ex}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ── Tech Stack ────────────────────────────────────────────────────────────────
function TechStack() {
  const [active, setActive] = useState(0);
  const [hov, setHov] = useState(null);
  const cat = TECH_STACK[active];

  return (
    <section style={{ padding: "100px 40px", background: "#fff" }} className="lms-section">
      <div style={{ maxWidth: 1280, margin: "0 auto" }}>
        <div style={{ textAlign: "center", marginBottom: 56 }}>
          <span style={{ fontSize: 12, fontWeight: 700, color: "#059669", letterSpacing: ".12em", textTransform: "uppercase", fontFamily: "'DM Sans',sans-serif" }}>Technology</span>
          <h2 style={{ fontFamily: "'Fraunces',serif", fontSize: "clamp(28px,4vw,46px)", fontWeight: 900, color: "#0F172A", marginTop: 10, marginBottom: 16, letterSpacing: "-0.025em" }}>Built on Proven Technology</h2>
          <p style={{ color: "#6B7280", fontSize: 16, maxWidth: 520, margin: "0 auto", lineHeight: 1.8, fontFamily: "'DM Sans',sans-serif" }}>Enterprise-grade technology stack powering every LMS we build — scalable, secure, and future-proof.</p>
        </div>
        <div style={{ display: "flex", justifyContent: "center", gap: 10, flexWrap: "wrap", marginBottom: 44 }}>
          {TECH_STACK.map((t, i) => (
            <button key={t.category} onClick={() => { setActive(i); setHov(null); }} style={{
              padding: "10px 22px", borderRadius: 99, fontWeight: 700, fontSize: 13.5,
              cursor: "pointer", border: "2px solid", transition: "all .25s",
              fontFamily: "'DM Sans',sans-serif",
              background: active === i ? "linear-gradient(135deg,#064E3B,#059669)" : "#fff",
              borderColor: active === i ? "#059669" : "#D1FAE5",
              color: active === i ? "#fff" : "#6B7280",
              boxShadow: active === i ? "0 6px 20px rgba(5,150,105,.3)" : "none",
            }}>{t.category}</button>
          ))}
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(160px,1fr))", gap: 18 }}>
          {cat.items.map((tech, i) => (
            <div key={tech.name}
              onMouseEnter={() => setHov(i)}
              onMouseLeave={() => setHov(null)}
              style={{
                display: "flex", flexDirection: "column", alignItems: "center", gap: 14,
                padding: "32px 20px 24px", borderRadius: 22,
                border: `2px solid ${hov === i ? tech.color : "#F0FDF4"}`,
                background: hov === i ? `${tech.color}07` : "#FAFAFA",
                boxShadow: hov === i ? `0 14px 40px ${tech.color}22` : "0 1px 4px rgba(0,0,0,.04)",
                transition: "all .3s cubic-bezier(.4,0,.2,1)",
                transform: hov === i ? "translateY(-7px) scale(1.03)" : "none",
                cursor: "default", position: "relative", overflow: "hidden",
              }}>
              <div style={{ width: 72, height: 72, display: "flex", alignItems: "center", justifyContent: "center", borderRadius: 18, background: hov === i ? `${tech.color}14` : "#fff", border: `1px solid ${hov === i ? tech.color + "28" : "#E5E7EB"}`, boxShadow: hov === i ? `0 4px 18px ${tech.color}22` : "0 1px 4px rgba(0,0,0,.06)", transition: "all .3s" }}>
                <img src={tech.logo} alt={tech.name} style={{ width: 44, height: 44, objectFit: "contain", transition: "transform .3s", transform: hov === i ? "scale(1.12)" : "scale(1)" }}
                  onError={e => { e.target.style.display = "none"; e.target.parentNode.innerHTML = `<span style="font-size:22px;font-weight:900;color:${tech.color};font-family:'Fraunces',serif">${tech.name[0]}</span>`; }}
                />
              </div>
              <div style={{ textAlign: "center" }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: hov === i ? tech.color : "#1E293B", fontFamily: "'DM Sans',sans-serif", transition: "color .25s" }}>{tech.name}</div>
                {hov === i && <div style={{ fontSize: 10, color: tech.color, fontWeight: 700, letterSpacing: ".06em", textTransform: "uppercase", marginTop: 3, fontFamily: "'DM Sans',sans-serif" }}>Expert Level</div>}
              </div>
              <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, height: 3, borderRadius: "0 0 20px 20px", background: `linear-gradient(90deg,${tech.color},${tech.color}60)`, transform: hov === i ? "scaleX(1)" : "scaleX(0)", transformOrigin: "left", transition: "transform .32s" }} />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ── Process ───────────────────────────────────────────────────────────────────
function ProcessSection() {
  return (
    <section style={{ padding: "100px 40px", background: "linear-gradient(150deg,#022C22 0%,#064E3B 45%,#0F172A 100%)" }} className="lms-section">
      <div style={{ maxWidth: 1280, margin: "0 auto" }}>
        <div style={{ textAlign: "center", marginBottom: 76 }}>
          <span style={{ fontSize: 12, fontWeight: 700, color: "#6EE7B7", letterSpacing: ".12em", textTransform: "uppercase", fontFamily: "'DM Sans',sans-serif" }}>Our Process</span>
          <h2 style={{ fontFamily: "'Fraunces',serif", fontSize: "clamp(28px,4vw,46px)", fontWeight: 900, color: "#fff", marginTop: 10, marginBottom: 16, letterSpacing: "-0.025em" }}>From Brief to 5M Learners</h2>
          <p style={{ color: "rgba(255,255,255,.55)", fontSize: 16, maxWidth: 520, margin: "0 auto", lineHeight: 1.8, fontFamily: "'DM Sans',sans-serif" }}>A structured 6-stage delivery methodology that de-risks your LMS build and guarantees a platform learners actually use.</p>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(300px,1fr))", gap: 24 }}>
          {PROCESS.map((step) => (
            <div key={step.n} style={{
              background: "rgba(255,255,255,.05)", borderRadius: 24, padding: "34px 30px",
              border: `1px solid rgba(255,255,255,.08)`,
              backdropFilter: "blur(12px)",
              position: "relative", overflow: "hidden",
              transition: "all .32s ease",
            }}
              onMouseEnter={e => { e.currentTarget.style.background = "rgba(5,150,105,.14)"; e.currentTarget.style.borderColor = `${step.color}55`; e.currentTarget.style.transform = "translateY(-4px)"; }}
              onMouseLeave={e => { e.currentTarget.style.background = "rgba(255,255,255,.05)"; e.currentTarget.style.borderColor = "rgba(255,255,255,.08)"; e.currentTarget.style.transform = "none"; }}
            >
              <div style={{ position: "absolute", top: 22, right: 26, fontFamily: "'Fraunces',serif", fontSize: 68, fontWeight: 900, color: "rgba(255,255,255,.03)", lineHeight: 1, pointerEvents: "none" }}>{step.n}</div>
              <div style={{ width: 58, height: 58, borderRadius: 16, background: `${step.color}22`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 26, marginBottom: 22, border: `1.5px solid ${step.color}38` }}>
                {step.icon}
              </div>
              <span style={{ fontSize: 11, fontWeight: 800, color: step.color, background: `${step.color}1A`, padding: "3px 12px", borderRadius: 99, display: "inline-block", marginBottom: 12, letterSpacing: ".04em", fontFamily: "'DM Sans',sans-serif" }}>STEP {step.n}</span>
              <h3 style={{ fontFamily: "'Fraunces',serif", fontWeight: 800, fontSize: 18, color: "#fff", marginBottom: 10, letterSpacing: "-0.02em" }}>{step.title}</h3>
              <p style={{ color: "rgba(255,255,255,.55)", fontSize: 14, lineHeight: 1.8, fontFamily: "'DM Sans',sans-serif" }}>{step.desc}</p>
              <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, height: 3, background: `linear-gradient(90deg,${step.color},${step.color}44)` }} />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ── Case Studies ──────────────────────────────────────────────────────────────
function CaseStudies() {
  const [hov, setHov] = useState(null);
  return (
    <section id="case-studies" style={{ padding: "100px 40px", background: "#fff" }} className="lms-section">
      <div style={{ maxWidth: 1280, margin: "0 auto" }}>
        <div style={{ textAlign: "center", marginBottom: 68 }}>
          <span style={{ fontSize: 12, fontWeight: 700, color: "#059669", letterSpacing: ".12em", textTransform: "uppercase", fontFamily: "'DM Sans',sans-serif" }}>Case Studies</span>
          <h2 style={{ fontFamily: "'Fraunces',serif", fontSize: "clamp(28px,4vw,46px)", fontWeight: 900, color: "#0F172A", marginTop: 10, marginBottom: 16, letterSpacing: "-0.025em" }}>Learning Platforms That Delivered</h2>
          <p style={{ color: "#6B7280", fontSize: 16, maxWidth: 520, margin: "0 auto", lineHeight: 1.8, fontFamily: "'DM Sans',sans-serif" }}>Real numbers from real LMS deployments — in production, serving real learners, driving real outcomes.</p>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 64 }}>
          {CASE_STUDIES.map((cs, idx) => (
            <div key={cs.title} style={{
              display: "grid", gridTemplateColumns: "1fr 1fr",
              gap: 56, alignItems: "center",
              direction: idx % 2 === 0 ? "ltr" : "rtl",
            }} className="lms-cs-grid">
              <div style={{ direction: "ltr", position: "relative", borderRadius: 24, overflow: "hidden", boxShadow: `0 32px 80px ${cs.color}22` }}
                onMouseEnter={() => setHov(cs.title)}
                onMouseLeave={() => setHov(null)}
              >
                <img src={cs.img} alt={cs.title} style={{ width: "100%", height: 380, objectFit: "cover", display: "block", transition: "transform .5s ease", transform: hov === cs.title ? "scale(1.05)" : "scale(1)" }} />
                <div style={{ position: "absolute", inset: 0, background: `linear-gradient(to top, ${cs.color}44, transparent 55%)` }} />
                <div style={{ position: "absolute", top: 20, left: 20, background: cs.color, color: "#fff", fontSize: 12, fontWeight: 800, padding: "5px 16px", borderRadius: 99, fontFamily: "'DM Sans',sans-serif" }}>{cs.industry}</div>
              </div>
              <div style={{ direction: "ltr" }}>
                <h3 style={{ fontFamily: "'Fraunces',serif", fontSize: "clamp(24px,3vw,38px)", fontWeight: 900, color: "#0F172A", marginBottom: 14, letterSpacing: "-0.025em" }}>{cs.title}</h3>
                <p style={{ color: "#6B7280", fontSize: 16, lineHeight: 1.85, marginBottom: 26, fontFamily: "'DM Sans',sans-serif" }}>{cs.desc}</p>
                <div style={{ display: "flex", gap: 7, flexWrap: "wrap", marginBottom: 30 }}>
                  {cs.tags.map(t => (
                    <span key={t} style={{ background: `${cs.color}0F`, color: cs.color, fontSize: 12, fontWeight: 700, padding: "5px 13px", borderRadius: 99, border: `1px solid ${cs.color}22`, fontFamily: "'DM Sans',sans-serif" }}>{t}</span>
                  ))}
                </div>
                <div style={{ display: "flex", gap: 32, marginBottom: 34, flexWrap: "wrap" }}>
                  {cs.metrics.map(m => (
                    <div key={m.lbl}>
                      <div style={{ fontFamily: "'Fraunces',serif", fontWeight: 900, fontSize: 30, color: cs.color, lineHeight: 1, letterSpacing: "-0.03em" }}>{m.val}</div>
                      <div style={{ fontSize: 12, color: "#6B7280", fontWeight: 600, marginTop: 5, fontFamily: "'DM Sans',sans-serif" }}>{m.lbl}</div>
                    </div>
                  ))}
                </div>
                <a href="#" style={{
                  display: "inline-flex", alignItems: "center", gap: 8,
                  background: cs.color, color: "#fff",
                  padding: "13px 28px", borderRadius: 12, fontWeight: 700, fontSize: 14,
                  textDecoration: "none", boxShadow: `0 6px 20px ${cs.color}32`,
                  transition: "transform .22s, box-shadow .22s", fontFamily: "'DM Sans',sans-serif",
                }}
                  onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = `0 10px 28px ${cs.color}44`; }}
                  onMouseLeave={e => { e.currentTarget.style.transform = "none"; e.currentTarget.style.boxShadow = `0 6px 20px ${cs.color}32`; }}
                >Read Full Case Study →</a>
              </div>
            </div>
          ))}
        </div>
        <div style={{ textAlign: "center", marginTop: 64 }}>
          <Link to="/portfolio" style={{
            background: "#fff", color: "#059669", border: "2px solid #A7F3D0",
            padding: "14px 36px", borderRadius: 12, fontWeight: 700, fontSize: 15,
            textDecoration: "none", fontFamily: "'DM Sans',sans-serif", transition: "all .22s", display: "inline-block",
          }}
            onMouseEnter={e => { e.currentTarget.style.background = "#F0FDF4"; e.currentTarget.style.borderColor = "#6EE7B7"; }}
            onMouseLeave={e => { e.currentTarget.style.background = "#fff"; e.currentTarget.style.borderColor = "#A7F3D0"; }}
          >View All LMS Projects →</Link>
        </div>
      </div>
    </section>
  );
}

// ── FAQ ───────────────────────────────────────────────────────────────────────
function FAQ() {
  const [open, setOpen] = useState(null);
  return (
    <section style={{ padding: "100px 40px", background: "#F0FDF4" }} className="lms-section">
      <div style={{ maxWidth: 860, margin: "0 auto" }}>
        <div style={{ textAlign: "center", marginBottom: 64 }}>
          <span style={{ fontSize: 12, fontWeight: 700, color: "#059669", letterSpacing: ".12em", textTransform: "uppercase", fontFamily: "'DM Sans',sans-serif" }}>FAQ</span>
          <h2 style={{ fontFamily: "'Fraunces',serif", fontSize: "clamp(28px,4vw,46px)", fontWeight: 900, color: "#0F172A", marginTop: 10, marginBottom: 16, letterSpacing: "-0.025em" }}>Frequently Asked Questions</h2>
          <p style={{ color: "#6B7280", fontSize: 16, lineHeight: 1.7, fontFamily: "'DM Sans',sans-serif" }}>Honest answers to the most common questions about custom LMS development.</p>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {FAQS.map((faq, i) => (
            <div key={i} style={{
              background: "#fff", borderRadius: 18,
              border: `2px solid ${open === i ? "#059669" : "#D1FAE5"}`,
              overflow: "hidden", transition: "border-color .25s, box-shadow .25s",
              boxShadow: open === i ? "0 8px 36px rgba(5,150,105,.1)" : "0 1px 4px rgba(0,0,0,.04)",
            }}>
              <button onClick={() => setOpen(open === i ? null : i)} style={{
                width: "100%", padding: "22px 30px", background: "none", border: "none",
                display: "flex", justifyContent: "space-between", alignItems: "center",
                cursor: "pointer", fontFamily: "'DM Sans',sans-serif",
              }}>
                <span style={{ fontWeight: 700, fontSize: 15.5, color: "#0F172A", textAlign: "left", lineHeight: 1.45, flex: 1, marginRight: 18 }}>{faq.q}</span>
                <div style={{
                  width: 34, height: 34, borderRadius: "50%",
                  background: open === i ? "linear-gradient(135deg,#064E3B,#059669)" : "#ECFDF5",
                  display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, transition: "all .28s",
                }}>
                  <span style={{ color: open === i ? "#fff" : "#059669", fontSize: 19, lineHeight: 1, display: "block", transform: open === i ? "rotate(45deg)" : "none", transition: "transform .28s" }}>+</span>
                </div>
              </button>
              {open === i && (
                <div style={{ padding: "0 30px 26px", animation: "fadeIn .22s ease" }}>
                  <p style={{ color: "#6B7280", fontSize: 14.5, lineHeight: 1.85, borderTop: "1px solid #ECFDF5", paddingTop: 18, fontFamily: "'DM Sans',sans-serif" }}>{faq.a}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ── Contact ───────────────────────────────────────────────────────────────────
function ContactCTA() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", type: "", desc: "" });
  const [focused, setFocused] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleChange = e => setForm(f => ({ ...f, [e.target.name]: e.target.value }));
  const handleSubmit = e => { e.preventDefault(); setLoading(true); setTimeout(() => { setLoading(false); setSubmitted(true); }, 1600); };

  const iStyle = (n) => ({
    width: "100%", padding: "13px 16px", borderRadius: 12, fontSize: 14,
    border: `2px solid ${focused === n ? "#059669" : "#D1FAE5"}`,
    background: focused === n ? "#ECFDF5" : "#F9FFFE",
    color: "#0F172A", outline: "none", fontFamily: "'DM Sans',sans-serif",
    transition: "all .22s", boxShadow: focused === n ? "0 0 0 4px rgba(5,150,105,.09)" : "none",
  });

  return (
    <section id="contact" style={{ padding: "100px 40px", background: "linear-gradient(150deg,#F0FDF4,#ECFDF5,#F0FDFA)" }} className="lms-section">
      <div style={{ maxWidth: 1200, margin: "0 auto" }}>
        <div style={{ textAlign: "center", marginBottom: 64 }}>
          <span style={{ fontSize: 12, fontWeight: 700, color: "#059669", letterSpacing: ".12em", textTransform: "uppercase", fontFamily: "'DM Sans',sans-serif" }}>Get Started</span>
          <h2 style={{ fontFamily: "'Fraunces',serif", fontSize: "clamp(28px,4vw,48px)", fontWeight: 900, color: "#0F172A", marginTop: 10, marginBottom: 14, letterSpacing: "-0.025em" }}>Launch Your LMS in 90 Days</h2>
          <p style={{ color: "#6B7280", fontSize: 16, maxWidth: 520, margin: "0 auto", lineHeight: 1.8, fontFamily: "'DM Sans',sans-serif" }}>Tell us about your learners and training goals — we'll deliver a free LMS scope and demo within 48 hours.</p>
        </div>
        <div className="lms-contact-grid">
          {/* Info panel */}
          <div>
            <div style={{ background: "linear-gradient(135deg,#022C22,#064E3B,#065F46)", borderRadius: 28, padding: "40px 36px", marginBottom: 24, boxShadow: "0 24px 64px rgba(5,150,105,.28)", position: "relative", overflow: "hidden" }}>
              <div style={{ position: "absolute", top: -60, right: -60, width: 240, height: 240, borderRadius: "50%", background: "rgba(255,255,255,.04)" }} />
              <div style={{ position: "absolute", bottom: -40, left: -40, width: 200, height: 200, borderRadius: "50%", background: "rgba(13,148,136,.08)" }} />
              <h3 style={{ fontFamily: "'Fraunces',serif", fontWeight: 800, fontSize: 22, color: "#fff", marginBottom: 8, position: "relative", letterSpacing: "-0.025em" }}>Free LMS Scope & Demo</h3>
              <p style={{ color: "rgba(255,255,255,.65)", fontSize: 14, lineHeight: 1.8, marginBottom: 32, position: "relative", fontFamily: "'DM Sans',sans-serif" }}>Share your learning programme — we'll scope the platform, recommend the architecture, and demo a similar build. No commitment required.</p>
              {[
                { icon: "📞", label: "Phone", val: "+91 96462 06032" },
                { icon: "✉️", label: "Email", val: "info@techvanto.com" },
                { icon: "📍", label: "Location", val: "New Delhi, India 🇮🇳" },
                { icon: "⏰", label: "Hours", val: "Mon–Sat, 9AM – 7PM IST" },
              ].map(item => (
                <div key={item.label} style={{ display: "flex", gap: 14, marginBottom: 18, alignItems: "flex-start", position: "relative" }}>
                  <div style={{ width: 40, height: 40, borderRadius: 12, background: "rgba(255,255,255,.13)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16, flexShrink: 0 }}>{item.icon}</div>
                  <div>
                    <div style={{ fontSize: 10, color: "rgba(255,255,255,.42)", fontWeight: 800, textTransform: "uppercase", letterSpacing: ".08em", marginBottom: 2, fontFamily: "'DM Sans',sans-serif" }}>{item.label}</div>
                    <div style={{ fontSize: 13.5, color: "#fff", fontWeight: 600, fontFamily: "'DM Sans',sans-serif" }}>{item.val}</div>
                  </div>
                </div>
              ))}
            </div>
            <div style={{ background: "#fff", borderRadius: 24, padding: "28px", border: "1.5px solid #D1FAE5", boxShadow: "0 4px 16px rgba(5,150,105,.07)" }}>
              <h4 style={{ fontFamily: "'Fraunces',serif", fontWeight: 800, fontSize: 16, color: "#0F172A", marginBottom: 18, letterSpacing: "-0.02em" }}>What You Get — Free</h4>
              {[
                ["🎓", "LMS feature scope tailored to your use case"],
                ["🏗️", "Architecture recommendation & tech stack"],
                ["💰", "Investment estimate & timeline"],
                ["🔒", "NDA signed before any discussion"],
                ["🖥️", "Live demo of a similar LMS we've built"],
              ].map(([icon, text]) => (
                <div key={text} style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 13 }}>
                  <span style={{ fontSize: 16 }}>{icon}</span>
                  <span style={{ fontSize: 13.5, color: "#374151", fontWeight: 500, fontFamily: "'DM Sans',sans-serif" }}>{text}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Form */}
          <div style={{ background: "#fff", borderRadius: 28, padding: "46px", boxShadow: "0 8px 56px rgba(5,150,105,.08)", border: "1.5px solid #D1FAE5" }}>
            {submitted ? (
              <div style={{ textAlign: "center", padding: "40px 0" }}>
                <div style={{ width: 92, height: 92, borderRadius: "50%", background: "linear-gradient(135deg,#064E3B,#059669,#0D9488)", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 24px", fontSize: 42, boxShadow: "0 14px 40px rgba(5,150,105,.35)" }}>🎓</div>
                <h3 style={{ fontFamily: "'Fraunces',serif", fontSize: 26, fontWeight: 900, color: "#0F172A", marginBottom: 12, letterSpacing: "-0.025em" }}>Demo Requested! 🎉</h3>
                <p style={{ color: "#6B7280", fontSize: 15, lineHeight: 1.75, maxWidth: 320, margin: "0 auto 28px", fontFamily: "'DM Sans',sans-serif" }}>Our LMS team will review your requirements and reach out with a custom demo and scope within 48 hours.</p>
                <button onClick={() => { setSubmitted(false); setForm({ name: "", email: "", phone: "", type: "", desc: "" }); }} style={{ background: "linear-gradient(135deg,#064E3B,#059669)", color: "#fff", border: "none", padding: "13px 30px", borderRadius: 12, fontWeight: 700, fontSize: 14, cursor: "pointer", fontFamily: "'DM Sans',sans-serif" }}>Submit Another Request</button>
              </div>
            ) : (
              <>
                <h3 style={{ fontFamily: "'Fraunces',serif", fontWeight: 900, fontSize: 22, color: "#0F172A", marginBottom: 6, letterSpacing: "-0.025em" }}>Tell Us About Your Training Needs</h3>
                <p style={{ color: "#9CA3AF", fontSize: 13.5, marginBottom: 32, fontFamily: "'DM Sans',sans-serif" }}>The more you share about your learners and goals, the more tailored our LMS recommendation will be.</p>
                <form onSubmit={handleSubmit}>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 16 }} className="lms-form-row">
                    <div>
                      <label style={{ display: "block", fontSize: 11.5, fontWeight: 700, color: "#374151", marginBottom: 7, textTransform: "uppercase", letterSpacing: ".07em", fontFamily: "'DM Sans',sans-serif" }}>Full Name *</label>
                      <input name="name" value={form.name} onChange={handleChange} onFocus={() => setFocused("name")} onBlur={() => setFocused(null)} placeholder="Your name" required style={iStyle("name")} />
                    </div>
                    <div>
                      <label style={{ display: "block", fontSize: 11.5, fontWeight: 700, color: "#374151", marginBottom: 7, textTransform: "uppercase", letterSpacing: ".07em", fontFamily: "'DM Sans',sans-serif" }}>Work Email *</label>
                      <input type="email" name="email" value={form.email} onChange={handleChange} onFocus={() => setFocused("email")} onBlur={() => setFocused(null)} placeholder="name@company.com" required style={iStyle("email")} />
                    </div>
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 16 }} className="lms-form-row">
                    <div>
                      <label style={{ display: "block", fontSize: 11.5, fontWeight: 700, color: "#374151", marginBottom: 7, textTransform: "uppercase", letterSpacing: ".07em", fontFamily: "'DM Sans',sans-serif" }}>Phone *</label>
                      <input type="tel" name="phone" value={form.phone} onChange={handleChange} onFocus={() => setFocused("phone")} onBlur={() => setFocused(null)} placeholder="+91 XXXXX XXXXX" required style={iStyle("phone")} />
                    </div>
                    <div>
                      <label style={{ display: "block", fontSize: 11.5, fontWeight: 700, color: "#374151", marginBottom: 7, textTransform: "uppercase", letterSpacing: ".07em", fontFamily: "'DM Sans',sans-serif" }}>LMS Type</label>
                      <select name="type" value={form.type} onChange={handleChange} onFocus={() => setFocused("type")} onBlur={() => setFocused(null)} style={{ ...iStyle("type"), appearance: "none", cursor: "pointer" }}>
                        <option value="">Select type…</option>
                        {["EdTech / School Platform", "Corporate L&D", "Healthcare / CME", "Course Marketplace", "Government Training", "Professional Certification", "Retail / Franchise Training", "Not sure yet"].map(p => <option key={p} value={p}>{p}</option>)}
                      </select>
                    </div>
                  </div>
                  <div style={{ marginBottom: 28 }}>
                    <label style={{ display: "block", fontSize: 11.5, fontWeight: 700, color: "#374151", marginBottom: 7, textTransform: "uppercase", letterSpacing: ".07em", fontFamily: "'DM Sans',sans-serif" }}>Learner & Training Description *</label>
                    <textarea name="desc" value={form.desc} onChange={handleChange} onFocus={() => setFocused("desc")} onBlur={() => setFocused(null)} placeholder="How many learners? What type of content (video, SCORM, live classes)? Any compliance or certification needs? What does success look like for your organisation?" rows={5} required style={{ ...iStyle("desc"), resize: "vertical", minHeight: 130, lineHeight: 1.75 }} />
                  </div>
                  <p style={{ fontSize: 12, color: "#9CA3AF", marginBottom: 20, fontFamily: "'DM Sans',sans-serif" }}>🔒 Your requirements are completely confidential. We sign NDAs and never share your information.</p>
                  <button type="submit" disabled={loading} style={{
                    width: "100%", padding: "16px", borderRadius: 14,
                    background: loading ? "#A7F3D0" : "linear-gradient(135deg,#064E3B,#059669,#0D9488)",
                    color: "#fff", border: "none", cursor: loading ? "not-allowed" : "pointer",
                    fontWeight: 800, fontSize: 16, fontFamily: "'DM Sans',sans-serif",
                    boxShadow: loading ? "none" : "0 8px 28px rgba(5,150,105,.38)",
                    transition: "all .25s", display: "flex", alignItems: "center", justifyContent: "center", gap: 10,
                  }}>
                    {loading ? "Submitting…" : "🎓 Get Free LMS Demo"}
                  </button>
                </form>
              </>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

// ── Footer ────────────────────────────────────────────────────────────────────
function Footer() {
  const cols = [
    { title: "Solutions", links: ["CRM Solution", "ERP Solution", "HRM Solution", "LMS Solution", "POS Solution", "SaaS Platform"] },
    { title: "Company", links: ["About Us", "Portfolio", "Blog", "Careers", "Contact", "Privacy Policy"] },
    { title: "Technologies", links: ["React & Next.js", "Flutter & RN", "Node.js & Django", "AWS & GCP", "Docker & K8s", "PostgreSQL"] },
  ];
  return (
    <footer style={{ background: "#011A12", color: "#fff", padding: "76px 40px 32px" }}>
      <div style={{ maxWidth: 1280, margin: "0 auto" }}>
        <div className="footer-grid" style={{ marginBottom: 56 }}>
          <div>
            <div style={{ marginBottom: 16 }}>
              <img src={LOGO_SRC} alt="Techvanto" style={{ height: 140, width: "auto", objectFit: "contain", filter: "brightness(0) invert(1)", display: "block" }} />
            </div>
            <p style={{ color: "#94A3B8", lineHeight: 1.8, fontSize: 14, maxWidth: 300, marginBottom: 24, fontFamily: "'DM Sans',sans-serif" }}>
              Building world-class digital products for businesses across 20+ countries. Innovation. Scale. Excellence.
            </p>
            <div style={{ display: "flex", gap: 12 }}>
              {["𝕏", "in", "𝔾", "▶"].map((icon, i) => (
                <a key={i} href="#" style={{ width: 40, height: 40, borderRadius: 12, background: "rgba(255,255,255,.07)", display: "flex", alignItems: "center", justifyContent: "center", color: "#94A3B8", textDecoration: "none", fontSize: 14, fontWeight: 700, transition: "all .22s" }}
                  onMouseEnter={e => { e.currentTarget.style.background = "#059669"; e.currentTarget.style.color = "#fff"; }}
                  onMouseLeave={e => { e.currentTarget.style.background = "rgba(255,255,255,.07)"; e.currentTarget.style.color = "#94A3B8"; }}
                >{icon}</a>
              ))}
            </div>
          </div>
          {cols.map(col => (
            <div key={col.title}>
              <h4 style={{ fontFamily: "'Fraunces',serif", fontWeight: 800, fontSize: 15, color: "#fff", marginBottom: 20, letterSpacing: "-0.01em" }}>{col.title}</h4>
              {col.links.map(l => (
                <a key={l} href="#" style={{ display: "block", color: "#64748B", fontSize: 13.5, textDecoration: "none", marginBottom: 10, transition: "color .15s", fontFamily: "'DM Sans',sans-serif" }}
                  onMouseEnter={e => e.target.style.color = "#6EE7B7"}
                  onMouseLeave={e => e.target.style.color = "#64748B"}
                >{l}</a>
              ))}
            </div>
          ))}
        </div>
        <div style={{ borderTop: "1px solid rgba(5,150,105,.15)", paddingTop: 28, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12 }}>
          <p style={{ color: "#475569", fontSize: 13, fontFamily: "'DM Sans',sans-serif" }}>© 2025 Techvanto Private Limited. All rights reserved.</p>
          <p style={{ color: "#475569", fontSize: 13, fontFamily: "'DM Sans',sans-serif" }}>Made with ❤️ in India 🇮🇳</p>
        </div>
      </div>
    </footer>
  );
}

// ── Root ──────────────────────────────────────────────────────────────────────
export default function LMSSolution() {
  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Fraunces:ital,opsz,wght@0,9..144,400;0,9..144,700;0,9..144,800;0,9..144,900;1,9..144,700&family=DM+Sans:wght@400;500;600;700;800&display=swap');

        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
        html { scroll-behavior: smooth; }
        body { font-family: 'DM Sans', -apple-system, sans-serif; background: #fff; color: #0F172A; overflow-x: hidden; }

        @keyframes fadeIn     { from { opacity:0; transform:translateY(14px); } to { opacity:1; transform:none; } }
        @keyframes float      { 0%,100% { transform:translateY(0); } 50% { transform:translateY(-12px); } }
        @keyframes floatSlow  { 0%,100% { transform:translateY(0) rotate(0deg); } 50% { transform:translateY(-18px) rotate(3deg); } }
        @keyframes morphFloat { 0%,100% { border-radius:30% 70% 70% 30%/30% 30% 70% 70%; transform:translateY(0); } 50% { border-radius:60% 40% 30% 70%/60% 30% 70% 40%; transform:translateY(-14px); } }
        @keyframes pulse      { 0%,100% { opacity:1; } 50% { opacity:.35; } }

        .nav-links        { display:flex; align-items:center; gap:4px; }
        .nav-cta          { display:flex !important; }
        .mob-menu-btn     { display:none; }

        .lms-hero-grid    { display:grid; grid-template-columns:1fr 1fr; gap:64px; align-items:center; }
        .lms-stats-grid   { display:grid; grid-template-columns:repeat(4,1fr); gap:32px; }
        .lms-cs-grid      { display:grid; grid-template-columns:1fr 1fr; gap:56px; align-items:center; }
        .lms-contact-grid { display:grid; grid-template-columns:1fr 1.55fr; gap:48px; align-items:start; }
        .footer-grid      { display:grid; grid-template-columns:2fr 1fr 1fr 1fr; gap:48px; }
        .lms-form-row     { display:grid; grid-template-columns:1fr 1fr; gap:16px; }

        @media (max-width:1100px) {
          .lms-hero-grid  { gap:40px; }
          .footer-grid    { grid-template-columns:1fr 1fr; gap:32px; }
          .lms-stats-grid { grid-template-columns:repeat(2,1fr); }
        }
        @media (max-width:900px) {
          .lms-hero-grid    { grid-template-columns:1fr; gap:48px; }
          .lms-cs-grid      { grid-template-columns:1fr !important; gap:36px !important; direction:ltr !important; }
          .lms-contact-grid { grid-template-columns:1fr; gap:32px; }
          .footer-grid      { grid-template-columns:1fr 1fr; gap:32px; }
          .nav-links        { display:none !important; }
          .nav-cta          { display:none !important; }
          .mob-menu-btn     { display:flex !important; }
        }
        @media (max-width:640px) {
          .footer-grid    { grid-template-columns:1fr; }
          .lms-stats-grid { grid-template-columns:repeat(2,1fr); gap:24px; }
          .lms-form-row   { grid-template-columns:1fr !important; }
        }
        @media (max-width:500px)  { .topbar-right { display:none !important; } }
        @media (max-width:900px)  { .lms-section  { padding-left:20px !important; padding-right:20px !important; } }
        @media (max-width:640px)  { .lms-section  { padding-left:16px !important; padding-right:16px !important; padding-top:64px !important; padding-bottom:64px !important; } }
      `}</style>

      <Hero />
      <StatsBar />
      <FeaturesGrid />
      <UseCases />
      <TechStack />
      <ProcessSection />
      <CaseStudies />
      <FAQ />
    </>
  );
}